import React, { useState } from 'react';
import { FullFieldContext } from './FieldEditor';

// ---------------------------------------------------------------------------
// Python literal conversion
// ---------------------------------------------------------------------------
function toPythonLiteral(value: unknown): string {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    // Escape backslashes and double-quotes, wrap in double quotes
    const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map((item) => `        ${toPythonLiteral(item)}`).join(',\n');
    return `[\n${items},\n    ]`;
  }
  return JSON.stringify(value);
}

// ---------------------------------------------------------------------------
// Diff generation
// ---------------------------------------------------------------------------
function computeDiff(
  canonical: FullFieldContext,
  edited: FullFieldContext
): Array<{ key: string; canonical: unknown; proposed: unknown }> {
  return (Object.keys(edited) as Array<keyof FullFieldContext>)
    .filter((key) => JSON.stringify(canonical[key]) !== JSON.stringify(edited[key]))
    .map((key) => ({ key, canonical: canonical[key], proposed: edited[key] }));
}

function buildPythonDiff(
  topic: string,
  diffs: Array<{ key: string; canonical: unknown; proposed: unknown }>
): string {
  if (diffs.length === 0) {
    return `# No changes — proposed config matches canonical for topic: "${topic}"`;
  }

  const timestamp = new Date().toISOString();
  const entries = diffs
    .map(({ key, canonical, proposed }) => {
      const oldComment = `    # CHANGED from: ${toPythonLiteral(canonical)}`;
      const newLine = `    "${key}": ${toPythonLiteral(proposed)},`;
      return `${oldComment}\n${newLine}`;
    })
    .join('\n\n');

  return `# Field Context Diff for topic: "${topic}"
# Apply to TOPIC_FIELD_CONTEXTS["${topic}"] in lambda_function_dual.py
# Generated: ${timestamp}
#
# Changed keys only (shallow merge):
updates = {
${entries}
}

# To apply:
# TOPIC_FIELD_CONTEXTS["${topic}"].update(updates)`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface DiffExportPanelProps {
  topic: string;
  canonical: FullFieldContext | null;
  edited: FullFieldContext | null;
}

const DiffExportPanel: React.FC<DiffExportPanelProps> = ({ topic, canonical, edited }) => {
  const [copied, setCopied] = useState(false);

  if (!canonical || !edited) {
    return (
      <div className="text-sm text-gray-400 italic py-4 text-center">
        Select a topic to see the export diff.
      </div>
    );
  }

  const diffs = computeDiff(canonical, edited);
  const pythonCode = buildPythonDiff(topic, diffs);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pythonCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS environments
      const el = document.createElement('textarea');
      el.value = pythonCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Python Diff</span>
          {diffs.length > 0 ? (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
              {diffs.length} field{diffs.length !== 1 ? 's' : ''} changed
            </span>
          ) : (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
              no changes
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className={`text-xs px-3 py-1 rounded border transition-colors ${
            copied
              ? 'bg-green-50 border-green-300 text-green-700'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>

      <pre className="bg-gray-900 text-green-400 font-mono text-xs rounded p-4 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
        {pythonCode}
      </pre>

      {diffs.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Share this with the deployment team to promote your changes to{' '}
          <code className="bg-gray-100 px-1 rounded">TOPIC_FIELD_CONTEXTS</code> in{' '}
          <code className="bg-gray-100 px-1 rounded">lambda_function_dual.py</code>.
        </p>
      )}
    </div>
  );
};

export default DiffExportPanel;
